import { Activity, Heart, AlertCircle, Calendar, Stethoscope } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { KeyMetrics } from "@/types/vision360";

interface Vision360KeyMetricsProps {
  metrics: KeyMetrics;
}

export function Vision360KeyMetrics({ metrics }: Vision360KeyMetricsProps) {
  const hasVitalSigns = metrics.vitalSigns && metrics.vitalSigns.length > 0;
  const hasChronicConditions = metrics.chronicConditions && metrics.chronicConditions.length > 0;
  const hasExamFindings = metrics.lastExamFindings && metrics.lastExamFindings.length > 0;
  const hasLastAppointment = !!metrics.lastAppointmentDate;

  const isEmpty = !hasVitalSigns && !hasChronicConditions && !hasExamFindings && !hasLastAppointment;

  if (isEmpty) {
    return (
      <div className="text-center py-4 text-muted-foreground text-sm">
        Sem dados clínicos chave disponíveis
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
        <Activity className="h-4 w-4" />
        Dados Chave
      </h4>

      <div className="grid grid-cols-2 gap-2">
        {/* Sinais Vitais */}
        {hasVitalSigns && (
          <Card className="col-span-2 p-3">
            <div className="flex items-center gap-2 mb-2">
              <Heart className="h-4 w-4 text-red-500" />
              <span className="text-xs font-medium">Sinais Vitais</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {metrics.vitalSigns!.map((vs, i) => (
                <div
                  key={i}
                  className={cn(
                    "p-2 rounded-lg text-center",
                    vs.status === "alterado"
                      ? "bg-red-50 dark:bg-red-950/30"
                      : "bg-muted/50"
                  )}
                >
                  <p className="text-xs text-muted-foreground">{vs.name}</p>
                  <p className={cn(
                    "text-sm font-bold",
                    vs.status === "alterado" && "text-red-600 dark:text-red-400"
                  )}>
                    {vs.value}
                  </p>
                  {vs.status === "alterado" && (
                    <AlertCircle className="h-3 w-3 text-red-500 mx-auto mt-0.5" />
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Condições Crônicas */}
        {hasChronicConditions && (
          <Card className="p-3">
            <div className="flex items-center gap-2 mb-2">
              <Stethoscope className="h-4 w-4 text-primary" />
              <span className="text-xs font-medium">Condições Crônicas</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {metrics.chronicConditions!.map((condition, i) => (
                <Badge key={i} variant="secondary" className="text-xs">
                  {condition}
                </Badge>
              ))}
            </div>
          </Card>
        )}

        {/* Último Atendimento */}
        {hasLastAppointment && (
          <Card className="p-3">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="h-4 w-4 text-primary" />
              <span className="text-xs font-medium">Último Atendimento</span>
            </div>
            <p className="text-sm font-medium">
              {new Date(metrics.lastAppointmentDate!).toLocaleDateString("pt-BR")}
            </p>
          </Card>
        )}

        {/* Achados de Exames */}
        {hasExamFindings && (
          <Card className="col-span-2 p-3">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="h-4 w-4 text-primary" />
              <span className="text-xs font-medium">Últimos Achados</span>
            </div>
            <ul className="space-y-1">
              {metrics.lastExamFindings!.slice(0, 3).map((finding, i) => (
                <li key={i} className="text-xs text-muted-foreground flex items-start gap-1">
                  <span className="text-primary">•</span>
                  <span>{finding}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </div>
  );
}
